import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from 'config';
import { Address, AddressValidationStatus, ManualAddress, Perils } from 'types/address';

import { defaultHeadersCommercialInsurance } from './defaultHeaders';
import { createCustomBaseQuery } from './utils/customBaseQuery';

export interface AddressMonikerResponse {
  buildingName: string;
  streetAddress1: string;
  city: string;
  stateCode: string;
  postalCode: string;
}

export interface GetAddressesRequest {
  searchTerm: string;
}

export interface GetAddressesResponse {
  fullPicklistMoniker: string;
  addressList: Address[];
  total: number;
}

export interface GetFullAddressRequest {
  moniker: string;
}

export interface GetFullAddressResponse {
  moniker: string;
  address: AddressMonikerResponse;
}

export interface ManualAddressValidationRequest {
  address: ManualAddress;
}

export interface ManualAddressValidationResponse {
  address: AddressValidationStatus;
}

export interface PerilLookupAddressRequest {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  effectiveDate: string;
}

export interface PerilLookupMonikerRequest {
  moniker: string;
  effectiveDate: string;
}

export interface PerilLookupResponse {
  perils: Perils;
}

export enum PerilSearchType {
  address = 'address',
  moniker = 'moniker',
}

export interface PerilLookupRequest {
  type: PerilSearchType;
  requestPayload: PerilLookupMonikerRequest | PerilLookupAddressRequest;
}

const customBaseQuery = createCustomBaseQuery({
  baseUrl: config.addressLookupApi,
  defaultHeaders: defaultHeadersCommercialInsurance,
  reducerPath: 'addressApi',
});
/* Define a service using a base URL and expected endpoints */
export const addressApi = createApi({
  reducerPath: 'addressApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getAddresses: builder.query<GetAddressesResponse, GetAddressesRequest>({
      query: ({ searchTerm }) => `/addresses?searchTerm=${searchTerm}`,
    }),
    getFullAddress: builder.query<GetFullAddressResponse, GetFullAddressRequest>({
      query: ({ moniker }) => `/addresses?moniker=${moniker}`,
    }),
    manualAddressValidation: builder.mutation<
      ManualAddressValidationResponse,
      ManualAddressValidationRequest
    >({
      query: (body) => ({
        url: '/addresses/validate',
        method: 'POST',
        body,
      }),
    }),
    perilLookup: builder.mutation<PerilLookupResponse, PerilLookupRequest>({
      query: ({ type, requestPayload }) => ({
        url: `/addresses/perils/search?searchType=${type}`,
        method: 'POST',
        body: requestPayload,
      }),
    }),
  }),
});

export const {
  useLazyGetAddressesQuery,
  useLazyGetFullAddressQuery,
  useManualAddressValidationMutation,
  usePerilLookupMutation,
} = addressApi;
